/**
 * Translation value processing utilities
 * Handles simple strings, nested objects, pluralization, and context
 */

// import type { TranslationParams } from '../types/translation.types.js';
// import { interpolateParams } from './translation-utils.js';
import { logger } from './logger.js';


/**
 * ✅ Resolve nested key path in translation object
 */
/**
 * ✅ Resolve nested key from namespace object
 * Examples:
 * - resolveNestedKey({user: {profile: {name: "John"}}}, "user.profile.name") → "John"
 * - resolveNestedKey({items: [{name: "Apple"}]}, "items[0].name") → "Apple"
 * - resolveNestedKey({welcome: "Hello"}, "welcome") → "Hello"
 */
export function resolveNestedKey(
  namespace: Record<string, unknown>,
  keyPath: string
): unknown {
  if (!namespace || !keyPath) {
    return undefined;
  }

  // Handle simple key (no nesting)
  if (!keyPath.includes('.') && !keyPath.includes('[')) {
    return namespace[keyPath];
  }

  // Handle nested key navigation
  try {
    const keys = parseKeyPath(keyPath);
    let current: unknown = namespace;

    for (const key of keys) {
      if (current === null || current === undefined) {
        logger.debug(
          `🔍 Nested key resolution stopped at null/undefined for: ${keyPath}`
        );
        return undefined;
      }

      if (typeof key === 'number') {
        // Array index access
        if (!Array.isArray(current)) {
          logger.debug(
            `🔍 Expected array for index ${key} in path: ${keyPath}`
          );
          return undefined;
        }
        current = current[key];
      } else {
        // Object property access
        if (typeof current !== 'object') {
          logger.debug(
            `🔍 Expected object for key '${key}' in path: ${keyPath}`
          );
          return undefined;
        }
        current = (current as Record<string, unknown>)[key];
      }
    }

    logger.debug(
      `🔍 Nested key resolved: ${keyPath} → ${typeof current === 'object' ? '[Object]' : current}`
    );
    return current;
  } catch (error) {
    logger.debug(`🔍 Nested key resolution error for '${keyPath}':`, error);
    return undefined;
  }
}

/**
 * ✅ Parse key path into array of keys/indices
 * Examples:
 * - "user.profile.name" → ["user", "profile", "name"]
 * - "items[0].name" → ["items", 0, "name"]
 * - "settings.ui.theme" → ["settings", "ui", "theme"]
 */
export function parseKeyPath(keyPath: string): (string | number)[] {
  const keys: (string | number)[] = [];
  let currentKey = '';
  let inBrackets = false;

  for (let i = 0; i < keyPath.length; i++) {
    const char = keyPath[i];

    if (char === '.' && !inBrackets) {
      // End of current key
      if (currentKey) {
        keys.push(currentKey);
        currentKey = '';
      }
    } else if (char === '[') {
      // Start of array index
      if (currentKey) {
        keys.push(currentKey);
        currentKey = '';
      }
      inBrackets = true;
    } else if (char === ']' && inBrackets) {
      // End of array index
      if (currentKey) {
        const index = parseInt(currentKey, 10);
        if (!isNaN(index)) {
          keys.push(index);
        } else {
          // Non-numeric index, treat as string
          keys.push(currentKey);
        }
        currentKey = '';
      }
      inBrackets = false;
    } else {
      // Regular character
      currentKey += char;
    }
  }

  // Add remaining key
  if (currentKey) {
    keys.push(currentKey);
  }

  return keys;
}


/**
 * ✅ Create individual cache key: locale:namespace:key
 */
export function createIndividualCacheKey(
  locale: string,
  namespace: string | undefined,
  key: string
): string {
  const parts = [locale];
  if (namespace) {
    parts.push(namespace);
  }
  parts.push(key);
  return parts.join(':');
}

/**
 * ✅ Create namespace cache key: @locale:namespace
 */
export function createNamespaceCacheKey(
  locale: string,
  namespace: string
): string {
  return `@${locale}:${namespace}`;
}

/**
 * ✅ Check if key is namespace cache entry
 */
export function isNamespaceCacheKey(key: string): boolean {
  return key.startsWith('@');
}

/**
 * ✅ Parse namespace cache key
 */
export function parseNamespaceCacheKey(
  key: string
): { locale: string; namespace: string } | null {
  if (!key.startsWith('@')) return null;

  const parts = key.substring(1).split(':');
  if (parts.length !== 2) return null;

  const [locale, namespace] = parts;
  return { locale, namespace };
}

/**
 * ✅ Parse individual cache key
 */
export function parseIndividualCacheKey(
  key: string
): { locale: string; namespace?: string; translationKey: string } | null {
  if (key.startsWith('@')) return null;

  const parts = key.split(':');
  if (parts.length < 2) return null;

  if (parts.length === 2) {
    const [locale, translationKey] = parts;
    return { locale, translationKey };
  }

  if (parts.length === 3) {
    const [locale, namespace, translationKey] = parts;
    return { locale, namespace, translationKey };
  }

  return null;
}

/**
 * ✅ Flatten nested translations for caching individual keys
 * Handles both ICU MessageFormat strings and structured objects
 * Stores full ContentUnit as JSON - includes both text and styles
 */
export function flattenTranslations(
  translations: Record<string, unknown>,
  prefix = '',
  locale: string,
  namespace: string
): Record<string, string> {
  const flattened: Record<string, string> = {};

  for (const [key, value] of Object.entries(translations)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const cacheKey = createIndividualCacheKey(locale, namespace, fullKey);

    if (typeof value === 'object' && value !== null && 'text' in value && 'styles' in value) {
      // Server returns ContentUnit format: { text, styles }
      // Store as JSON to preserve both text and styles
      flattened[cacheKey] = JSON.stringify(value);
    } else if (typeof value === 'object' && value !== null) {
      // Nested object - flatten recursively
      Object.assign(
        flattened,
        flattenTranslations(value as Record<string, unknown>, fullKey, locale, namespace)
      );
    } else {
      // Fallback for plain strings - wrap in ContentUnit format
      flattened[cacheKey] = JSON.stringify({ text: String(value), styles: {} });
    }
  }

  return flattened;
}
